import type { ReactNode } from 'react';

import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  type Operation,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { ApolloProvider } from '@apollo/client/react';
import { cacheSizes, getMainDefinition } from '@apollo/client/utilities';
import { getFragmentDefinitions } from '@apollo/client/utilities/internal';
import type { ErrorsEmitter } from '@realadvisor/error';
import { useConstant } from '@realadvisor/hooks';
import {
  type IntrospectionInputObjectType,
  type IntrospectionObjectType,
  type IntrospectionQuery,
  type IntrospectionTypeRef,
  Kind,
  type SelectionSetNode,
  type TypeNode,
} from 'graphql';
import { createNanoEvents } from 'nanoevents';
import { from as observableFrom, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import * as config from '@shared/config';

import { shouldRetryAfterRefresh } from './jwt-retry';
import { PAGE_HEADER, operationTaggedUri, pageRoute } from './request-tags';
import { getSessionToken, refreshSessionOrReload } from './session';

// Apollo v4 replaces the v3 `resultCacheMaxSize` (single LRU cap of
// 100_000 we used previously) with per-cache `cacheSizes` knobs, with
// significantly lower defaults (`executeSelectionSet` is 50_000 in v4
// vs 65_536 in v3, `executeSubSelectedArray` is 10_000). Restore the
// larger budget here to keep the prior cache-hit behavior on read-heavy
// CRM screens. Must be set before any InMemoryCache is constructed.
cacheSizes['inMemoryCache.executeSelectionSet'] = 100_000;
cacheSizes['inMemoryCache.executeSubSelectedArray'] = 100_000;

// only do this in dev mode // local dev only
let TYPE_MAP: Map<string, IntrospectionObjectType> | undefined = undefined;
let INPUT_TYPE_MAP: Map<string, IntrospectionInputObjectType> | undefined =
  undefined;
let JSON_SCHEMA: IntrospectionQuery | undefined = undefined;
if (import.meta.env.DEV === true) {
  import('./__generated__/graphql.schema.json').then(
    module => (JSON_SCHEMA = module.default as unknown as IntrospectionQuery),
  );
}

export const getSchema = (operation: Operation, cache?: InMemoryCache) => {
  if (JSON_SCHEMA != null) {
    return JSON_SCHEMA.__schema;
  }

  const operationCache =
    cache ?? (operation.getContext().cache as InMemoryCache | undefined);
  const normalizedCache = operationCache?.extract();
  const schema = normalizedCache?.['schema']?.['__schema'] as
    | IntrospectionQuery['__schema']
    | undefined;

  // The introspection schema is fetched once at startup (SchemaInitializer) and
  // stored under the `schema` cache key. Schema-dependent links silently no-op
  // when it's missing, producing subtly broken queries (e.g. a property
  // duplication stripped of all its fields). Surface it loudly instead of
  // failing silently — but skip the introspection bootstrap itself, where an
  // absent schema is expected.
  if (
    schema == null &&
    operation.operationName?.includes('IntrospectionQuery') !== true
  ) {
    console.error(
      `[getSchema] Introspection schema unavailable for operation "${
        operation.operationName ?? 'unknown'
      }"; schema-dependent links will be skipped.`,
    );
  }

  return schema;
};

const addIdToSelectionSet = (
  selectionSet: SelectionSetNode | undefined,
  typeOrObject: string,
  typeMap: Map<string, IntrospectionObjectType>,
  parentType: string,
): void => {
  if (!selectionSet) {
    return;
  }

  if (typeOrObject.endsWith('_aggregate')) {
    return;
  }

  // Helper function to find a field's type name
  const getFieldTypeName = (
    parentTypeName: string,
    fieldName: string,
  ): string | null => {
    const type = typeMap.get(parentTypeName);
    if (!type) {
      return null;
    }

    const field = type.fields?.find(f => f.name === fieldName);
    if (!field) {
      return null;
    }

    // Unwrap non-null and list types
    let currentType = field.type;
    while ('ofType' in currentType && currentType.ofType) {
      currentType = currentType.ofType;
    }
    return 'name' in currentType ? currentType.name : null;
  };

  // Function to check if a nested path has an ID field
  const checkNestedPathForId = (path: string): boolean => {
    const parts = path.split('.');
    let currentTypeName = 'query_root';

    for (const part of parts) {
      // If we're at the first part and it's not found in query_root,
      // try looking up the type directly in the type map
      if (currentTypeName === 'query_root') {
        // Check type map first since it's a direct lookup
        const type = typeMap.get(part);
        if (type) {
          currentTypeName = part;
          continue;
        }

        // Fall back to checking field type if not found in type map
        const nextTypeName = getFieldTypeName(currentTypeName, part);
        if (!nextTypeName) {
          return false;
        }
        currentTypeName = nextTypeName;
      } else {
        const nextTypeName = getFieldTypeName(currentTypeName, part);
        if (!nextTypeName) {
          return false;
        }
        currentTypeName = nextTypeName;
      }
    }

    const type = typeMap.get(currentTypeName);
    return (
      (type?.kind === 'OBJECT' && type.fields?.some(f => f.name === 'id')) ||
      false
    );
  };

  // Check if current type has ID and add if needed
  const currentTypeHasId = checkNestedPathForId(
    `${parentType}.${typeOrObject}`,
  );

  if (currentTypeHasId) {
    const hasIdSelection = selectionSet.selections.some(
      selection =>
        selection.kind === Kind.FIELD && selection.name.value === 'id',
    );

    if (!hasIdSelection) {
      selectionSet.selections = [
        {
          kind: Kind.FIELD,
          name: {
            kind: Kind.NAME,
            value: 'id',
          },
        },
        ...selectionSet.selections,
      ];
    }
  }

  // Process nested selections
  for (const selection of selectionSet.selections) {
    if (selection.kind === 'Field' && selection.selectionSet) {
      addIdToSelectionSet(
        selection.selectionSet,
        selection.name.value,
        typeMap,
        parentType ? `${parentType}.${typeOrObject}` : typeOrObject,
      );
    }
  }
};

const getNestedType = (type: TypeNode): string => {
  if (type.kind === 'NamedType') {
    return type.name.value;
  }
  return getNestedType(type.type);
};

const getNestedInputType = (type: IntrospectionTypeRef) => {
  if (type.kind === 'NON_NULL' || type.kind === 'LIST') {
    return getNestedInputType(type.ofType);
  }

  return type;
};

const pickInputFields = (
  value: Record<string, unknown>,
  fields: string[],
): Record<string, unknown> =>
  Object.fromEntries(
    fields
      .filter(field => Object.prototype.hasOwnProperty.call(value, field))
      .map(field => [field, value[field]]),
  );

export const errorsEmitter: ErrorsEmitter = createNanoEvents();

export const fetchApiQuery = async (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> => {
  const headers = new Headers({
    ...(init?.headers ?? {}),
    'Content-Type': 'application/json',
    Accept: 'application/json',
  });
  const access_token = getSessionToken();
  if (access_token != null) {
    headers.set('Authorization', `Bearer ${access_token}`);
  }
  const request: RequestInit = {
    ...(init ?? {}),
    headers,
    credentials: 'include',
  };

  const response = await fetch(input, request);
  const responseClone = response.clone();
  const data = await responseClone.json();
  if (data.errors) {
    if (
      data.errors?.some((e: { message: string | string[] }) =>
        e.message.includes('JWT is expired'),
      ) &&
      (await refreshSessionOrReload())
    ) {
      return fetchApiQuery(input, request);
    } else {
      // A JWT error that cannot be retried surfaces like any other error.
      errorsEmitter.emit('add', input.toString(), data.errors);
    }
  } else if (data.error?.includes('JWT is expired')) {
    if (await refreshSessionOrReload()) {
      return fetchApiQuery(input, request);
    }
  }

  return response;
};

type Props = {
  children: ReactNode;
};

const createApolloClient = () => {
  const endpoint = `${config.hasura_origin}/v1/graphql`;
  const scrapersEndpoint = `${config.scrapers_origin}/v1/graphql`;
  const cache = new InMemoryCache({
    typePolicies: {
      cma_report_files: { keyFields: ['cma_report_id', 'file_id'] },
      property_files: { keyFields: ['property_id', 'file_id'] },
      development_collaborators: { keyFields: ['development_id', 'user_id'] },
      development_files: { keyFields: ['development_id', 'file_id'] },
      development_mappings: { keyFields: ['lot_id', 'image_id'] },
      ApiKey: { keyFields: ['PartnerID'] },
      LegacyApiKey: { keyFields: ['PartnerID'] },
      Query: {
        fields: {
          lots_by_pk: {
            read(_, { args, toReference }) {
              return toReference({
                __typename: 'lots',
                id: args?.id,
              });
            },
          },
          mls_lots: {
            read(existing, { args, toReference }) {
              if (existing != null) {
                return existing;
              }
              if (args?.where?.id?._eq != null && args?.limit === 1) {
                return [
                  toReference({
                    __typename: 'mls_lots',
                    id: args.where.id._eq,
                  }),
                ];
              }
              return undefined;
            },
          },
          dictionaries_by_pk: {
            read(_, { args, toReference }) {
              return toReference({
                __typename: 'dictionaries',
                id: args?.id,
              });
            },
          },
        },
      },
    },
  });

  const httpMainApiLink = new HttpLink({
    uri: endpoint,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  const httpScrapersLink = new HttpLink({
    uri: operation =>
      operationTaggedUri(scrapersEndpoint, operation.operationName),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  const httpLink = ApolloLink.split(
    operation => operation.getContext().clientName === 'scrapers',
    httpScrapersLink, //if above
    httpMainApiLink,
  );

  const authLink = new SetContextLink(({ headers }) => {
    // allow anonymous requests for tenant slug validation
    const access_token = getSessionToken();
    if (access_token == null) {
      return { headers };
    }

    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${access_token}`,
        // The screen that sent the request, for the request log (route only).
        [PAGE_HEADER]: pageRoute(window.location.pathname),
      },
    };
  });

  const errorLink = new ErrorLink(({ error, operation, forward }) => {
    if (CombinedGraphQLErrors.is(error)) {
      for (const err of error.errors) {
        if (shouldRetryAfterRefresh(err)) {
          // Retry only when the refresh answered 2xx: `refreshSessionOrReload`
          // resolves `false` on every other outcome, and reloads the tab when
          // the refresh wiped the tokens. Retrying anyway sent the query
          // without `Authorization`, so Hasura ran it as `anonymous`. Apollo v4
          // rejects a link chain that completes without a value, so re-emit the
          // original error.
          return observableFrom(refreshSessionOrReload()).pipe(
            mergeMap(canRetry =>
              canRetry ? forward(operation) : throwError(() => error),
            ),
          );
        }
        return;
      }
      return;
    }

    // Surface non-GraphQL errors (ServerError, ServerParseError, LinkError,
    // LocalStateError, ...) for visibility, but let the original typed error
    // propagate so downstream `.is()` checks still work. Re-throwing as a
    // generic Error would erase the typed class.
    console.error('[Apollo link error]', error);
  });

  const removeTypenameLink = new RemoveTypenameFromVariablesLink();

  const addIdsToQuery = new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);
    const fragments = getFragmentDefinitions(operation.query);

    if (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'query'
    ) {
      const schema = getSchema(operation, cache);

      if (!schema) {
        return forward(operation);
      }
      if (TYPE_MAP == null) {
        TYPE_MAP = new Map(
          schema.types.filter(t => t.kind === 'OBJECT').map(t => [t.name, t]),
        );
      }

      // Process definition selections
      if (definition.selectionSet?.selections) {
        for (const selection of definition.selectionSet.selections) {
          if (selection.kind === 'Field') {
            addIdToSelectionSet(
              selection.selectionSet,
              selection.name.value,
              TYPE_MAP,
              'query_root',
            );
          }
        }
      }

      // Process fragments
      for (const fragment of fragments) {
        addIdToSelectionSet(
          fragment.selectionSet,
          fragment.typeCondition.name.value,
          TYPE_MAP,
          'query_root',
        );
      }

      // ensure operation.query is updated by creating a new object
      operation.query = {
        ...operation.query,
        definitions: [...operation.query.definitions],
      };
    }

    return forward(operation);
  });

  // this link will prune mutation input variables to only include fields that are defined in the input class
  const pruneMutationInput = new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);
    if (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'mutation'
    ) {
      const schema = getSchema(operation, cache);

      if (!schema) {
        return forward(operation);
      }

      for (const variableDef of definition.variableDefinitions ?? []) {
        try {
          // go through all nested type until we find NamedType
          const typeName = getNestedType(variableDef.type);

          // thanks to TS 5.5, filter can infer proper type
          const inputObject = schema.types
            .filter(t => t.kind === 'INPUT_OBJECT')
            .find(t => t.name === typeName);

          const fields = inputObject?.inputFields.map(field => field.name);
          if (!fields || fields.length === 0) {
            continue;
          }

          const name = variableDef.variable.name.value;
          if (operation.variables?.[name] != null) {
            const variable = operation.variables[name];
            if (Array.isArray(variable)) {
              operation.variables[name] = variable.map(v => {
                if (v != null) {
                  return pickInputFields(v, fields);
                } else {
                  return v;
                }
              });
            } else {
              operation.variables[name] = pickInputFields(variable, fields);
            }
          }
        } catch {
          // do nothing => this middleware is best effort
        }
      }
    }
    return forward(operation);
  });

  const updateNestedDates = (typeName: string, value: any) => {
    const inputObject = INPUT_TYPE_MAP?.get(typeName);
    // We don't want to go down the "on_conflict" fields.
    const fields = inputObject?.inputFields.filter(
      ({ name }) => name !== 'on_conflict',
    );

    if (fields == null || fields.length === 0) {
      return;
    }

    for (const field of fields) {
      const type = getNestedInputType(field.type);

      const fieldValue = value[field.name];

      if (fieldValue == null) {
        continue;
      }

      // We don't support the type "date array" in our model, so if the nested type is a date, it shouldn't be an array.
      if (type.name === 'date') {
        const date = new Date(fieldValue);
        // We substract back the timezone offset so that we do not account for it when in UTC.
        date.setHours(date.getHours() - date.getTimezoneOffset() / 60);
        value[field.name] = date.toISOString();
      } else if (type.kind === 'INPUT_OBJECT') {
        const fieldValueArray = Array.isArray(fieldValue)
          ? fieldValue
          : [fieldValue];

        for (const item of fieldValueArray) {
          updateNestedDates(type.name, item);
        }
      }
    }
  };

  // this link will force the field of type "date only" (no time) to be converted to ISO string so we don't have a shift because of client timezone
  const forceDateIsoMutationInput = new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);

    if (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'mutation'
    ) {
      const schema = getSchema(operation, cache);

      if (!schema) {
        return forward(operation);
      }

      if (INPUT_TYPE_MAP == null) {
        INPUT_TYPE_MAP = new Map(
          schema.types
            .filter(t => t.kind === 'INPUT_OBJECT')
            .map(t => [t.name, t]),
        );
      }

      for (const variableDef of definition.variableDefinitions ?? []) {
        try {
          // go through all nested type until we find NamedType
          const typeName = getNestedType(variableDef.type);
          const variableName = variableDef.variable.name.value;

          if (operation.variables?.[variableName] == null) {
            continue;
          }

          const variableValue = operation.variables[variableName];

          if (typeName === 'date') {
            const date = new Date(variableValue);
            // We substract back the timezone offset so that we do not account for it when in UTC.
            date.setHours(date.getHours() - date.getTimezoneOffset() / 60);
            operation.variables[variableName] = date.toISOString();
          } else {
            updateNestedDates(typeName, variableValue);
          }
        } catch (error) {
          // do nothing => this middleware is best effort
          if (!config.isProduction) {
            console.error(error);
          }
        }
      }
    }

    return forward(operation);
  });

  // Allow us to pass a execute a custom link per query when provided through the context
  const customLink = new ApolloLink((operation, forward) => {
    const context = operation.getContext();

    if (
      context.customLink == null ||
      typeof context.customLink !== 'function'
    ) {
      return forward(operation);
    }

    // Apollo v4 no longer injects `cache` into the operation context, so a
    // custom link (e.g. getAddAllSchemaFieldsLink) calling getSchema(operation)
    // without an explicit cache would read undefined and silently skip its work.
    // Expose the cache here, where it's in scope, to restore the v3 behavior.
    operation.setContext({ cache });

    return context.customLink(operation, forward);
  });

  return new ApolloClient({
    link: ApolloLink.from([
      removeTypenameLink,
      addIdsToQuery,
      customLink,
      pruneMutationInput,
      forceDateIsoMutationInput,
      errorLink,
      authLink,
      httpLink,
    ]),
    cache,
    devtools: { enabled: import.meta.env.NODE_ENV !== 'production' },
    defaultOptions: {
      watchQuery: {
        // Keep Apollo v3's default: no extra emissions (re-renders) during
        // refetch/fetchMore. Side effect: in v4 this also suppresses the
        // loading=true emission of useLazyQuery, so any useLazyQuery whose
        // `loading` drives UI must pass notifyOnNetworkStatusChange: true.
        notifyOnNetworkStatusChange: false,
      },
    },
  });
};

export const ApolloHasuraWrapper = (props: Props) => {
  const client = useConstant(createApolloClient);
  return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
};
